import { Form, router } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { type ReactElement, useState } from "react"
import { useTranslation } from "react-i18next"

import { BadgeImage } from "@/components/badges/badge-image"
import { BrandImageUploader } from "@/components/brand-image-uploader"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { accentClasses } from "@/lib/badge-icons"
import { cn } from "@/lib/utils"
import { badgeImagePath, badgePath, badgesPath } from "@/routes"

const IMAGE_ACCEPT = "image/png,image/webp"
const IMAGE_MAX_BYTES = 2 * 1024 * 1024
const IMAGE_MIN_DIMENSION = 128

/**
 * OKS-50 / OKS-538 — Custom badge create/edit dialog.
 *
 * Inertia `<Form>` only — NO shadcn `Form`/`FormField` (Crew rule).
 * Plain shadcn `Input` / `Textarea` / `Label` carry `name` attrs that
 * map to Rails strong-params (`badge[name]` / `badge[description]` /
 * `badge[icon]` / `badge[accent_color]` / `badge[image]`). Errors flow
 * through the render-prop `errors` object.
 *
 * OKS-538: the icon-grid hero is replaced by a `<BrandImageUploader>` —
 * the badge is now an uploaded image (or an accent-tinted default). The
 * `icon` field is retained as a hidden default (still a required model
 * column) but is no longer surfaced; `accent_color` stays as a compact
 * swatch row because it drives the default tint. A file input in the
 * form auto-triggers a multipart submit. Removing a saved image goes
 * through the dedicated `DELETE /badges/:id/image` endpoint.
 */
interface CustomBadgeFormDialogProps {
  /**
   * The affordance that opens the dialog. Optional when the dialog is
   * driven in controlled mode via `open`/`onOpenChange` (e.g. from a
   * `DropdownMenuItem`), in which case no `DialogTrigger` is rendered.
   * A `ReactElement` (not `ReactNode`) because Base UI's `DialogTrigger`
   * `render` prop merges its props into a single element.
   */
  trigger?: ReactElement
  availableIcons: string[]
  availableColors: string[]
  mode?: "create" | "edit"
  badge?: {
    id: number
    name: string
    description: string
    icon: string
    accent_color: string
    image_url: string | null
  }
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Optional `data-testid` for the `DialogContent` root (boundary canaries). */
  dialogTestId?: string
}

export function CustomBadgeFormDialog({
  trigger,
  availableIcons,
  availableColors,
  mode = "create",
  badge,
  open: controlledOpen,
  onOpenChange,
  dialogTestId,
}: CustomBadgeFormDialogProps) {
  const { t } = useTranslation()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  const setOpen = (value: boolean) => {
    if (!isControlled) setUncontrolledOpen(value)
    onOpenChange?.(value)
  }

  const [name, setName] = useState(badge?.name ?? "")
  const [description, setDescription] = useState(badge?.description ?? "")
  const [icon] = useState(badge?.icon ?? availableIcons[0])
  const [color, setColor] = useState(badge?.accent_color ?? availableColors[0])

  const action = mode === "edit" && badge ? badgePath(badge.id) : badgesPath()
  const method = mode === "edit" ? "patch" : "post"

  // Client pre-check — a fast inline hint mirroring the server rules
  // (PNG/WebP, ≤2 MB, square ≥128 px). The server remains authoritative.
  const validateImage = (file: File): Promise<string | null> => {
    if (!IMAGE_ACCEPT.split(",").includes(file.type)) {
      return Promise.resolve(t("badges.form.image.errors.type"))
    }
    if (file.size > IMAGE_MAX_BYTES) {
      return Promise.resolve(t("badges.form.image.errors.size"))
    }
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file)
      const probe = new window.Image()
      probe.onload = () => {
        URL.revokeObjectURL(url)
        const square = probe.naturalWidth === probe.naturalHeight
        const big = probe.naturalWidth >= IMAGE_MIN_DIMENSION
        resolve(square && big ? null : t("badges.form.image.errors.dimensions"))
      }
      probe.onerror = () => {
        URL.revokeObjectURL(url)
        resolve(t("badges.form.image.errors.dimensions"))
      }
      probe.src = url
    })
  }

  // Remove a SAVED image (edit mode) → dedicated destroy endpoint. Purge
  // + redirect refreshes the props so every surface reverts to default.
  const savedImageBadge = mode === "edit" && badge?.image_url ? badge : null
  const onRemoveSaved = savedImageBadge
    ? () => {
        router.delete(badgeImagePath(savedImageBadge.id), {
          preserveScroll: true,
          onSuccess: () => setOpen(false),
        })
      }
    : undefined

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && trigger && <DialogTrigger render={trigger} />}
      <DialogContent
        data-testid={dialogTestId}
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "edit"
              ? t("badges.dialog.edit_title")
              : t("badges.dialog.create_title")}
          </DialogTitle>
        </DialogHeader>

        <Form
          action={action}
          method={method}
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
          data-testid="custom-badge-form"
          className="flex flex-col gap-4"
        >
          {({ errors, processing }) => (
            <>
              {/* Icon is a required model column but no longer surfaced —
                  the image (or accent-tint default) is the badge face. */}
              <input type="hidden" name="badge[icon]" value={icon} />
              <input type="hidden" name="badge[accent_color]" value={color} />

              {/* Badge image uploader — the primary visual (OKS-538). */}
              <div className="grid gap-2">
                <BrandImageUploader
                  currentUrl={badge?.image_url ?? null}
                  name="badge[image]"
                  label={t("badges.form.image.label")}
                  helper={t("badges.form.image.helper")}
                  removeLabel={t("badges.form.image.remove")}
                  accept={IMAGE_ACCEPT}
                  onRemove={onRemoveSaved}
                  error={errors.image}
                  validate={validateImage}
                  renderPreview={(url) => (
                    <BadgeImage
                      imageUrl={url}
                      accentColor={color}
                      name={name || t("badges.form.name.placeholder")}
                      size="modal"
                    />
                  )}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="badge_name">
                  {t("badges.form.name.label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  id="badge_name"
                  name="badge[name]"
                  type="text"
                  maxLength={60}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("badges.form.name.placeholder")}
                  aria-required="true"
                  aria-invalid={Boolean(errors.name)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("badges.form.name.counter", { count: name.length })}
                </p>
                <InputError messages={errors.name} />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="badge_description">
                  {t("badges.form.description.label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Textarea
                  id="badge_description"
                  name="badge[description]"
                  maxLength={200}
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("badges.form.description.placeholder")}
                  aria-required="true"
                  aria-invalid={Boolean(errors.description)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("badges.form.description.counter", {
                    count: description.length,
                  })}
                </p>
                <InputError messages={errors.description} />
              </div>

              {/* Accent colour — de-emphasized; drives the default tint. */}
              <div className="grid gap-2">
                <Label>{t("badges.form.color.label")}</Label>
                <div
                  className="flex gap-2"
                  role="radiogroup"
                  aria-label={t("badges.form.color.label")}
                >
                  {availableColors.map((swatch) => {
                    const palette = accentClasses(swatch)
                    const selected = swatch === color
                    return (
                      <button
                        key={swatch}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setColor(swatch)}
                        data-testid={`color-pick-${swatch}`}
                        className={cn(
                          "h-8 w-8 rounded-full ring-2 transition",
                          palette.bg,
                          selected
                            ? "ring-primary"
                            : "hover:ring-muted ring-transparent",
                        )}
                      >
                        <span className="sr-only">{swatch}</span>
                      </button>
                    )
                  })}
                </div>
                <InputError messages={errors.accent_color} />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing && (
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {mode === "edit"
                    ? t("badges.actions.edit")
                    : t("badges.actions.create")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
